import { useAuth0 } from '@auth0/auth0-react';
import type {
  PartnerNoti,
  Private,
  ServiceInput,
  ServicePayload,
} from '@seek/indie-api-types';
import { useCallback, useMemo } from 'react';
import type { SearchResultItem } from '#search/types.ts';

import {
  type EnvironmentConfig,
  useEnvironmentConfig,
} from '#src/hooks/environment.tsx';
import { useTracingHeaders } from '#src/hooks/tracingHeaders.tsx';

type NotiConfig = PartnerNoti.PartnerNotiConfig;
type NotiInput = PartnerNoti.PartnerNotiConfigInput;
type NotiHistory = PartnerNoti.HistoricNoti;

interface Client {
  name: string;
  clientId: string;
  hasClientGrant: boolean;
  createdAt: string;
}

export type CreateClientPayload = Client & {
  clientSecret: string;
};

export type LiveClientPayload = Client & {
  type: 'live';
  clientSecret: string | null;
  lastUsedAt?: string | null;
};

export type PublicTestClientPayload = Client & {
  type: 'publicTest';
  clientSecret: string;
  lastUsedAt?: string | null;
};

export type GetClientsPayload = Array<
  LiveClientPayload | PublicTestClientPayload
>;

export interface GetPartnerTokenPayload {
  accessToken: string;
  tokenType: string;
  expiresIn: number;
}

export class ApiError extends Error {
  statusCode: number;
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
    this.statusCode = status;
  }
}

const createApiClient = (
  environment: EnvironmentConfig,
  getToken: () => Promise<string>,
  tracingHeaders: Record<string, string>,
) => {
  const { baseUrls } = environment;

  const REQUEST_TIMEOUT_MS = 5000;

  interface RequestOptions {
    baseUrl: string;
    body?: string;
    method: string;
    path: string;
    timeout?: number;
    token?: string;
    signal?: AbortSignal;
  }

  const request = async ({
    baseUrl,
    body,
    method,
    path,
    timeout = REQUEST_TIMEOUT_MS,
    token,
    signal,
  }: RequestOptions) => {
    const authToken = token ?? (await getToken());
    const headers: Record<string, string> = {
      ...tracingHeaders,
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    };

    const uri = `${baseUrl}${path}`;

    const response = await fetch(uri, {
      body,
      method,
      headers: {
        ...(body && { 'Content-Type': 'application/json' }),
        ...headers,
      },
      signal: AbortSignal.any([
        AbortSignal.timeout(timeout),
        ...(signal ? [signal] : []),
      ]),
    });

    if (
      !response.ok &&
      response.status === 400 &&
      response.headers.get('content-type')?.indexOf('application/json') !== -1
    ) {
      const responseData = await response.json();

      if (!responseData.message) {
        throw new ApiError(
          `${path} responded with ${response.status}: ${response.statusText}`,
          response.status,
        );
      }

      throw new ApiError(responseData.message, response.status);
    }

    if (!response.ok) {
      throw new ApiError(
        `${path} responded with ${response.status}: ${response.statusText}`,
        response.status,
      );
    }

    return response;
  };

  const requestJson = async (opts: RequestOptions) => {
    const response = await request(opts);
    return response.json();
  };

  return {
    credentials: {
      list: (signal: AbortSignal): Promise<GetClientsPayload> =>
        requestJson({
          baseUrl: baseUrls.users,
          path: '/user-api/credentials/getClients',
          method: 'POST',
          signal,
        }),
      create: (
        name: string,
        dataType: 'live' | 'publicTest',
      ): Promise<CreateClientPayload> =>
        requestJson({
          baseUrl: baseUrls.users,
          path: '/user-api/credentials/createCredentials',
          method: 'POST',
          body: JSON.stringify({ name, dataType }),
        }),
      updateClient: (clientId: string, name: string) =>
        request({
          baseUrl: baseUrls.users,
          path: '/user-api/credentials/updateClient',
          method: 'POST',
          body: JSON.stringify({ clientId, name }),
        }),
      delete: (clientId: string) =>
        request({
          baseUrl: baseUrls.users,
          path: '/user-api/credentials/deleteClient',
          method: 'POST',
          body: JSON.stringify({ clientId }),
        }),
      createClientGrant: (clientId: string) =>
        request({
          baseUrl: baseUrls.users,
          path: '/user-api/credentials/createClientGrant',
          method: 'POST',
          body: JSON.stringify({ clientId }),
        }),
      getPartnerToken: (
        clientId: string,
        signal?: AbortSignal,
      ): Promise<GetPartnerTokenPayload> =>
        requestJson({
          baseUrl: baseUrls.users,
          path: '/user-api/credentials/getPartnerToken',
          method: 'POST',
          body: JSON.stringify({ clientId }),
          signal,
        }),
    },
    noti: {
      get: async (): Promise<NotiConfig | undefined> => {
        const response = await request({
          baseUrl: baseUrls.noti,
          path: '/noti/config',
          method: 'GET',
        });

        return response.status === 204 ? undefined : response.json();
      },
      update: (newDetails: NotiInput): Promise<NotiConfig> =>
        requestJson({
          baseUrl: baseUrls.noti,
          path: '/noti/config',
          method: 'POST',
          body: JSON.stringify(newDetails),
        }),
    },
    notiHistory: {
      get: (): Promise<NotiHistory[]> =>
        requestJson({
          baseUrl: baseUrls.noti,
          path: '/noti/history',
          method: 'GET',
        }),
    },
    organizations: {
      listInvitations: (
        signal: AbortSignal,
      ): Promise<Private.Auth0OrganizationInvitationPayload[]> =>
        requestJson({
          baseUrl: baseUrls.users,
          path: '/user-api/organizations/listInvitations',
          method: 'GET',
          signal,
        }),
      listUsers: (signal: AbortSignal): Promise<Private.Auth0UserPayload[]> =>
        requestJson({
          baseUrl: baseUrls.users,
          path: '/user-api/organizations/listUsers',
          method: 'GET',
          signal,
        }),
      listOrganizations: (
        signal: AbortSignal,
      ): Promise<Private.Auth0UserOrganizationPayload[]> =>
        requestJson({
          baseUrl: baseUrls.users,
          path: '/user-api/organizations/listOrganizations',
          method: 'GET',
          signal,
        }),
      addUser: (newUserDetails: Private.Auth0UserInput) =>
        request({
          baseUrl: baseUrls.users,
          path: '/user-api/organizations/addUser',
          method: 'POST',
          body: JSON.stringify(newUserDetails),
        }),
      changeRole: (newRoleDetails: Private.Auth0UserRoleChangeInput) =>
        request({
          baseUrl: baseUrls.users,
          path: '/user-api/organizations/changeUserRole',
          method: 'POST',
          body: JSON.stringify(newRoleDetails),
        }),
      removeSelf: (organizationId: string) =>
        request({
          baseUrl: baseUrls.users,
          path: '/user-api/organizations/removeSelf',
          method: 'POST',
          body: JSON.stringify({ id: organizationId }),
        }),
      removeUser: (userId: string) =>
        request({
          baseUrl: baseUrls.users,
          path: '/user-api/organizations/removeUser',
          method: 'POST',
          body: JSON.stringify({ id: userId }),
        }),
      deleteInvite: (inviteId: string) =>
        request({
          baseUrl: baseUrls.users,
          path: '/user-api/organizations/deleteInvitation',
          method: 'POST',
          body: JSON.stringify({ id: inviteId }),
        }),
      finishAccountSetup: (
        {
          password,
          name,
        }: {
          password: string;
          name: string;
        },
        token: string,
      ) =>
        request({
          baseUrl: baseUrls.users,
          path: '/user-api/organizations/finishAccountSetup',
          method: 'POST',
          body: JSON.stringify({ password, name }),
          token,
        }),
      resetMfa: (userId: string) =>
        request({
          baseUrl: baseUrls.users,
          path: '/user-api/organizations/resetMfa',
          method: 'POST',
          body: JSON.stringify({ id: userId }),
        }),
    },
    self: {
      resetMfa: () =>
        request({
          baseUrl: baseUrls.users,
          path: '/user-api/organizations/resetSelfMfa',
          method: 'POST',
        }),
      requestChangePassword: () =>
        request({
          baseUrl: baseUrls.users,
          path: '/user-api/users/changePassword',
          method: 'POST',
        }),
      changeName: (name: string) =>
        request({
          baseUrl: baseUrls.users,
          path: '/user-api/users/changeName',
          method: 'POST',
          body: JSON.stringify({ name }),
        }),
    },
    testEvents: {
      sendTestEvent: (
        input: Private.TestCallInput,
      ): Promise<Private.TestCallOutput> =>
        requestJson({
          baseUrl: baseUrls.users,
          path: '/user-api/events/test',
          method: 'POST',
          body: JSON.stringify(input),
        }),
    },
    subscriptions: {
      webhookHealth: async (
        input: Private.WebhookSubscriptionRequestMetricInput,
      ): Promise<Private.WebhookSubscriptionRequestMetric | undefined> => {
        const response = await request({
          baseUrl: baseUrls.users,
          path: '/user-api/subscriptions/health',
          method: 'POST',
          body: JSON.stringify(input),
        });
        return response.status === 204 ? undefined : response.json();
      },
    },
    securityBreach: {
      reportAuthenticated: async (input: Private.SecurityBreachInput) =>
        await request({
          baseUrl: baseUrls.users,
          path: '/security-breach/reportAuthenticated',
          method: 'POST',
          body: JSON.stringify(input),
        }),
      reportUnauthenticated: async (input: Private.SecurityBreachInput) =>
        await request({
          baseUrl: baseUrls.users,
          path: '/security-breach/reportUnauthenticated',
          method: 'POST',
          body: JSON.stringify(input),
        }),
    },
    grants: {
      list: (signal: AbortSignal): Promise<Private.GrantsPayload> =>
        requestJson({
          baseUrl: baseUrls.users,
          path: '/user-api/grants',
          method: 'GET',
          signal,
        }),
    },
    impersonation: {
      reset: async () =>
        await request({
          baseUrl: baseUrls.users,
          path: '/user-api/resetImpersonation',
          method: 'POST',
        }),
    },
    integrationSummaryStatistics: {
      get: (
        days: number,
        signal: AbortSignal,
      ): Promise<ServicePayload.IntegrationSummaryStatistics> =>
        requestJson({
          baseUrl: baseUrls.observability,
          path: `/summary?days=${days}`,
          method: 'POST',
          // Generally happier to wait for longer for observability things due to lower expectations.
          // Reindexing peaks can cause some slowdowns.
          timeout: 20000,
          signal,
        }),
    },
    integrationRequests: {
      get: (
        input: ServiceInput.IntegrationRequests,
        signal: AbortSignal,
      ): Promise<ServicePayload.IntegrationRequests> =>
        requestJson({
          baseUrl: baseUrls.observability,
          path: '/requests',
          method: 'POST',
          body: JSON.stringify(input),
          // Generally happier to wait for longer for observability things due to lower expectations.
          // Reindexing peaks can cause some slowdowns.
          timeout: 20000,
          signal,
        }),
    },
    integrationStatistics: {
      get: (
        input: ServiceInput.IntegrationStatistics,
        signal: AbortSignal,
      ): Promise<ServicePayload.IntegrationStatistics> =>
        requestJson({
          baseUrl: baseUrls.observability,
          path: '/statistics',
          method: 'POST',
          body: JSON.stringify(input),
          // Generally happier to wait for longer for observability things due to lower expectations.
          // Reindexing peaks can cause some slowdowns.
          timeout: 20000,
          signal,
        }),
    },
    siteSearch: {
      get: (term: string, signal: AbortSignal): Promise<SearchResultItem[]> =>
        requestJson({
          baseUrl: baseUrls.search,
          path: `/search?${new URLSearchParams({ q: term }).toString()}`,
          method: 'GET',
          timeout: 2000,
          signal,
        }),
    },
  };
};

export const useApi = (
  options: { dangerouslyDefeatAuth?: boolean } = {
    dangerouslyDefeatAuth: false,
  },
) => {
  const { dangerouslyDefeatAuth } = options;
  const { getAccessTokenSilently } = useAuth0();
  const env = useEnvironmentConfig();
  const tracingHeaders = useTracingHeaders();
  const getToken = useCallback(async () => {
    // Callers that opt out of auth target unauthenticated endpoints and may run
    // while logged out, so resolve empty and let `request` omit the header.
    if (dangerouslyDefeatAuth) {
      return '';
    }
    const token = await getAccessTokenSilently();
    if (!token) {
      throw new Error('Failed to get access token');
    }
    return token;
  }, [dangerouslyDefeatAuth, getAccessTokenSilently]);
  return useMemo(
    () => createApiClient(env, getToken, tracingHeaders),
    [env, getToken, tracingHeaders],
  );
};
